import { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  Link, AlignLeft, AlignCenter, AlignRight, Code, Eye,
  Type, Heading1, Heading2, Palette, Minus, Image as ImageIcon, Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RichTextEditorRef {
  insertAtCursor: (text: string) => void;
  insertImage: (url: string, alt?: string) => void;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  className?: string;
  /**
   * If provided, enables an "Insert image" toolbar button + drag&drop + paste
   * support. The handler must accept a File and return the URL to embed into
   * an <img src="..."> tag. Throwing an Error will surface its message via
   * `onImageError`.
   */
  onImageUpload?: (file: File) => Promise<string>;
  /**
   * Called when image upload validation/upload fails. Use this to surface a
   * toast in the host app. Receives a localized message.
   */
  onImageError?: (message: string) => void;
  /**
   * Allowed mime types for images. Defaults to PNG/JPEG/WebP.
   */
  imageAllowedTypes?: string[];
  /**
   * Max image size in bytes. Defaults to 1 MB (1024*1024).
   */
  imageMaxSizeBytes?: number;
}

const DEFAULT_IMAGE_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const DEFAULT_IMAGE_MAX_BYTES = 1024 * 1024;

function escapeHtmlAttr(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  value,
  onChange,
  placeholder = 'Scrivi qui...',
  minHeight = 200,
  className,
  onImageUpload,
  onImageError,
  imageAllowedTypes,
  imageMaxSizeBytes,
}, ref) => {
  const [mode, setMode] = useState<'richtext' | 'html' | 'preview'>('richtext');
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);
  const savedSelection = useRef<Range | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const allowedTypes = imageAllowedTypes && imageAllowedTypes.length > 0 ? imageAllowedTypes : DEFAULT_IMAGE_ALLOWED_TYPES;
  const maxBytes = typeof imageMaxSizeBytes === 'number' && imageMaxSizeBytes > 0 ? imageMaxSizeBytes : DEFAULT_IMAGE_MAX_BYTES;

  useEffect(() => {
    if (mode === 'richtext' && editorRef.current && !isInternalUpdate.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalUpdate.current = false;
  }, [value, mode]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalUpdate.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedSelection.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedSelection.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedSelection.current);
    }
  }, []);

  const insertAtCursor = useCallback((text: string) => {
    if (mode === 'html') {
      onChange(value + text);
      return;
    }
    if (mode === 'preview') return;

    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();

    const sel = window.getSelection();
    if (savedSelection.current && editor.contains(savedSelection.current.startContainer)) {
      sel?.removeAllRanges();
      sel?.addRange(savedSelection.current);
    } else if (!sel || sel.rangeCount === 0 || !editor.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    document.execCommand('insertText', false, text);
    saveSelection();
    handleInput();
  }, [mode, value, onChange, saveSelection, handleInput]);

  const insertImage = useCallback((url: string, alt?: string) => {
    if (!url) return;
    const safeUrl = escapeHtmlAttr(url);
    const safeAlt = escapeHtmlAttr(alt || 'image');
    const imgHtml = `<img src="${safeUrl}" alt="${safeAlt}" style="max-width:100%;height:auto;" />`;

    if (mode === 'html') {
      onChange((value || '') + imgHtml);
      return;
    }
    if (mode === 'preview') return;

    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();

    const sel = window.getSelection();
    if (savedSelection.current && editor.contains(savedSelection.current.startContainer)) {
      sel?.removeAllRanges();
      sel?.addRange(savedSelection.current);
    } else if (!sel || sel.rangeCount === 0 || !editor.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    document.execCommand('insertHTML', false, imgHtml);
    saveSelection();
    handleInput();
  }, [mode, value, onChange, saveSelection, handleInput]);

  useImperativeHandle(ref, () => ({ insertAtCursor, insertImage }), [insertAtCursor, insertImage]);

  const validateImageFile = useCallback((file: File): string | null => {
    const mime = (file.type || '').toLowerCase();
    if (!allowedTypes.includes(mime)) {
      return 'Formato non supportato. Usa PNG, JPEG o WebP.';
    }
    if (file.size > maxBytes) {
      return `Immagine troppo grande. Limite ${Math.round(maxBytes / 1024)}KB.`;
    }
    return null;
  }, [allowedTypes, maxBytes]);

  const handleImageFile = useCallback(async (file: File) => {
    if (!onImageUpload) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      onImageError?.(validationError);
      return;
    }
    try {
      setUploadingImage(true);
      const url = await onImageUpload(file);
      if (url) {
        insertImage(url, file.name);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Caricamento immagine fallito';
      onImageError?.(msg);
    } finally {
      setUploadingImage(false);
    }
  }, [onImageUpload, onImageError, validateImageFile, insertImage]);

  const handleImageButtonClick = useCallback(() => {
    if (!onImageUpload) return;
    fileInputRef.current?.click();
  }, [onImageUpload]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageFile(file);
    }
    e.target.value = '';
  }, [handleImageFile]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!onImageUpload) return;
    const files = Array.from(e.dataTransfer?.files || []);
    const imageFile = files.find(f => f.type.startsWith('image/'));
    if (imageFile) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      handleImageFile(imageFile);
    } else {
      setIsDragOver(false);
    }
  }, [onImageUpload, handleImageFile]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!onImageUpload) return;
    const items = Array.from(e.dataTransfer?.items || []);
    const hasImage = items.some(it => it.kind === 'file' && it.type.startsWith('image/'));
    if (hasImage) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  }, [onImageUpload]);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    if (!onImageUpload) return;
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(it => it.kind === 'file' && it.type.startsWith('image/'));
    if (imageItem) {
      const file = imageItem.getAsFile();
      if (file) {
        e.preventDefault();
        handleImageFile(file);
      }
    }
  }, [onImageUpload, handleImageFile]);

  const execCmd = useCallback((command: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleInput();
  }, [handleInput]);

  const handleLink = useCallback(() => {
    const url = prompt('Inserisci URL:', 'https://');
    if (url) execCmd('createLink', url);
  }, [execCmd]);

  const handleColor = useCallback(() => {
    const color = prompt('Colore (es. #ff0000, red):', '#000000');
    if (color) execCmd('foreColor', color);
  }, [execCmd]);

  type ToolbarButton =
    | { type: 'separator'; icon?: undefined; cmd?: undefined; title?: undefined; val?: undefined; action?: undefined }
    | { type?: undefined; icon: typeof Bold; cmd: string; title: string; val?: string; action?: 'link' | 'color' | 'image' };
  const toolbarButtons: ToolbarButton[] = [
    { icon: Bold, cmd: 'bold', title: 'Grassetto' },
    { icon: Italic, cmd: 'italic', title: 'Corsivo' },
    { icon: Underline, cmd: 'underline', title: 'Sottolineato' },
    { icon: Strikethrough, cmd: 'strikeThrough', title: 'Barrato' },
    { type: 'separator' },
    { icon: Heading1, cmd: 'formatBlock', val: 'h2', title: 'Titolo' },
    { icon: Heading2, cmd: 'formatBlock', val: 'h3', title: 'Sottotitolo' },
    { icon: Type, cmd: 'formatBlock', val: 'p', title: 'Paragrafo' },
    { type: 'separator' },
    { icon: List, cmd: 'insertUnorderedList', title: 'Elenco puntato' },
    { icon: ListOrdered, cmd: 'insertOrderedList', title: 'Elenco numerato' },
    { type: 'separator' },
    { icon: AlignLeft, cmd: 'justifyLeft', title: 'Allinea a sinistra' },
    { icon: AlignCenter, cmd: 'justifyCenter', title: 'Allinea al centro' },
    { icon: AlignRight, cmd: 'justifyRight', title: 'Allinea a destra' },
    { type: 'separator' },
    { icon: Link, cmd: 'link', title: 'Inserisci link', action: 'link' },
    { icon: Palette, cmd: 'color', title: 'Colore testo', action: 'color' },
    { icon: Minus, cmd: 'insertHorizontalRule', title: 'Linea orizzontale' },
    ...(onImageUpload
      ? [{ icon: ImageIcon, cmd: 'image', title: 'Inserisci immagine', action: 'image' as const }]
      : []),
  ];

  return (
    <div className={cn("border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden", className)}>
      <style>{`
        [data-rte-editor]:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-2 py-1">
        <div className="flex items-center gap-0.5 flex-wrap">
          {mode === 'richtext' && toolbarButtons.map((btn, i) => {
            if (btn.type === 'separator') {
              return <div key={i} className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />;
            }
            const Icon = btn.icon;
            const cmd = btn.cmd;
            return (
              <button
                key={i}
                type="button"
                title={btn.title}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (btn.action === 'link') handleLink();
                  else if (btn.action === 'color') handleColor();
                  else if (btn.action === 'image') handleImageButtonClick();
                  else execCmd(cmd, btn.val);
                }}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setMode('richtext')}
            className={cn(
              "px-2 py-1 text-xs rounded font-medium transition-colors",
              mode === 'richtext'
                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
          >
            <Type className="w-3 h-3 inline mr-1" />
            Rich Text
          </button>
          <button
            type="button"
            onClick={() => setMode('html')}
            className={cn(
              "px-2 py-1 text-xs rounded font-medium transition-colors",
              mode === 'html'
                ? "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
          >
            <Code className="w-3 h-3 inline mr-1" />
            HTML
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            className={cn(
              "px-2 py-1 text-xs rounded font-medium transition-colors",
              mode === 'preview'
                ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
          >
            <Eye className="w-3 h-3 inline mr-1" />
            Preview
          </button>
        </div>
      </div>

      {mode === 'richtext' && (
        <div className="relative">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            data-rte-editor=""
            className={cn(
              "p-3 outline-none text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900 prose prose-sm max-w-none dark:prose-invert transition-colors",
              isDragOver && onImageUpload && "ring-2 ring-blue-400 ring-inset bg-blue-50 dark:bg-blue-900/20"
            )}
            style={{ minHeight }}
            onInput={handleInput}
            onBlur={() => { saveSelection(); handleInput(); }}
            onKeyUp={saveSelection}
            onMouseUp={saveSelection}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onPaste={handlePaste}
            data-placeholder={placeholder}
          />
          {uploadingImage && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-gray-900/70 pointer-events-none">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm text-sm text-gray-700 dark:text-gray-200">
                <Loader2 className="w-4 h-4 animate-spin" />
                Caricamento immagine...
              </div>
            </div>
          )}
          {onImageUpload && (
            <input
              ref={fileInputRef}
              type="file"
              accept={allowedTypes.join(',')}
              className="hidden"
              onChange={handleFileInputChange}
            />
          )}
        </div>
      )}

      {mode === 'html' && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full p-3 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring text-sm font-mono text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900 resize-y border-0"
          style={{ minHeight }}
        />
      )}

      {mode === 'preview' && (
        <div
          className="p-3 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900 prose prose-sm max-w-none dark:prose-invert"
          style={{ minHeight }}
          dangerouslySetInnerHTML={{ __html: value || `<p class="text-gray-400">${placeholder}</p>` }}
        />
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;
